/* eslint-disable no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Quotation,
  QuotationCategory,
  QuotationParams,
  QuotationProduct,
  TemplateDetails,
  TemplateList
} from 'src/types/quotation';
import * as thunks from './thunks';
import reducers from './reducers';
import { initialQuotationState } from 'src/views/quotation/constants';
import { uniqBy } from 'lodash';

export type State = {
  category: {
    is_category_loading: boolean;
    categoryOptions: QuotationCategory[];
  };
  template: {
    is_template_loading: boolean;
    templateOptions: TemplateList[];
    templateDetails: TemplateDetails;
  };
  quotationData: Quotation[];

  product: {
    is_loading: boolean;
    productOptions: QuotationProduct[];
  };

  is_loading: boolean;
  quotationProductParams: QuotationParams;
  totalAmount: number;
  branch_id?: number;
};

const initialState: State = {
  category: {
    is_category_loading: false,
    categoryOptions: []
  },
  template: {
    is_template_loading: false,
    templateOptions: [],
    templateDetails: {
      id: 0,
      name: ''
    }
  },
  product: {
    is_loading: false,
    productOptions: []
  },
  is_loading: false,
  quotationData: initialQuotationState,
  quotationProductParams: {
    limit: 100,
    boost: 0,
    pc_build: 0,
    is_srp: false,
    currentSrpValue: { name: 'Promo Price', value: '0%' }
  },
  totalAmount: 0,
  branch_id: undefined
};

const slice = createSlice({
  name: 'quotations',
  initialState,
  reducers: reducers,
  extraReducers(builder) {
    builder.addCase(thunks.getQuotationsProductsThunk.pending, (state) => {
      const { product } = state;
      product.is_loading = true;
    });

    builder.addCase(thunks.getQuotationsProductsThunk.rejected, (state) => {
      const { product } = state;
      product.is_loading = false;
    });

    builder.addCase(
      thunks.getQuotationsProductsThunk.fulfilled,
      (state, { payload }) => {
        const { product } = state;
        const newData = payload?.originalData?.data || [];
        product.is_loading = false;

        product.productOptions = uniqBy(
          [...product.productOptions, ...newData],
          'product_id'
        );
      }
    );

    builder.addCase(thunks.getQuotationsCategoriesThunk.pending, (state) => {
      const { category } = state;
      category.is_category_loading = true;
    });

    builder.addCase(thunks.getQuotationsCategoriesThunk.rejected, (state) => {
      const { category } = state;
      category.is_category_loading = false;
    });

    builder.addCase(
      thunks.getQuotationsCategoriesThunk.fulfilled,
      (state, { payload }) => {
        const { category } = state;
        category.is_category_loading = false;
        category.categoryOptions = payload.originalData.categories || [];
      }
    );

    builder.addCase(thunks.getTemplateListThunk.pending, (state) => {
      const { template } = state;
      template.is_template_loading = true;
    });

    builder.addCase(thunks.getTemplateListThunk.rejected, (state) => {
      const { template } = state;
      template.is_template_loading = false;
    });

    builder.addCase(
      thunks.getTemplateListThunk.fulfilled,
      (state, { payload }) => {
        const { template } = state;
        template.is_template_loading = false;
        template.templateOptions = payload.originalData.data || [];
      }
    );

    builder.addCase(thunks.getTemplateDetailsThunk.pending, (state) => {
      const { template } = state;
      template.is_template_loading = true;
    });

    builder.addCase(thunks.getTemplateDetailsThunk.rejected, (state) => {
      const { template } = state;
      template.is_template_loading = false;
    });

    builder.addCase(
      thunks.getTemplateDetailsThunk.fulfilled,
      (state, { payload }) => {
        const templateDetails = payload.originalData.data || {
          id: 0,
          name: ''
        };

        state.template.templateDetails = templateDetails;
        state.template.is_template_loading = false;

        const templateProducts = templateDetails.products || [];

        state.quotationData = templateProducts.map((item) => ({
          quantity: item.quantity,
          product: item.product,
          name: item.product?.product_name || '',
          product_id: item.product?.product_id,
          slug: item.product?.slug || '',
          custom: item.custom || false,
          warning_note: item.warning_note || ''
        }));

        //initialize state params
        state.quotationProductParams = {
          ...state.quotationProductParams,
          boost: templateDetails?.boost ? 1 : 0,
          pc_build: templateDetails?.pc_build ? 1 : 0
        };
      }
    );

    builder.addCase(thunks.checkQuotationAvailabilityThunk.pending, (state) => {
      state.is_loading = true;
    });

    builder.addCase(
      thunks.checkQuotationAvailabilityThunk.rejected,
      (state) => {
        state.is_loading = false;
      }
    );

    builder.addCase(
      thunks.checkQuotationAvailabilityThunk.fulfilled,
      (state, { payload }) => {
        state.is_loading = false;
        state.quotationData =
          payload?.originalData?.quote_items || initialQuotationState;
      }
    );

    builder.addCase(
      thunks.onComputeSrpThunk.fulfilled,
      (state, { payload }) => {
        const srpData = payload?.originalData?.products_with_srp || [];

        //update with srp application default is false
        // state.quotationProductParams = {
        //   ...state.quotationProductParams,
        //   is_srp: !state.quotationProductParams.is_srp
        // };

        state.quotationData = state.quotationData.map((item) => {
          const srpItem = srpData.find((p) => p.product_id === item.product_id);
          if (srpItem) {
            return {
              ...item,
              product: {
                ...item.product,
                retail_price: srpItem.new_srp
              }
            };
          }
          return item;
        });
      }
    );
  }
});

export const reducer = slice.reducer;

export const actions = {
  ...slice.actions,
  ...thunks
};
